import React, {createContext, useContext, useRef} from "react";
import {createPortal} from "react-dom";
import {fromPickedFiles, SelectedFile} from "core/transfer/selection";
import {CaptureSource, SelectionLosses, selectionLossesOf} from "core/transfer/selectionLosses";
import {mergeSelectedFiles} from "../mergeFiles";
import {DropOverlay} from "./DropOverlay";
import {DroppedCapture} from "./dropSelection";
import {useWindowFileDrag} from "./useWindowFileDrag";

interface BrowseApi {
	browseFiles: () => void;
	/** Opens the DIRECTORY chooser (ui#1314). One call site per "Add a folder" button, nothing else. */
	browseFolder: () => void;
}
const BrowseContext = createContext<BrowseApi>({browseFiles: () => {}, browseFolder: () => {}});
export const useBrowse = () => useContext(BrowseContext);

interface Props {
	selection: SelectedFile[];
	onSelectionChange: (selection: SelectedFile[]) => void;
	/**
	 * Whether whole-page dragging is live. The design only accepts drops while `screen==='create'`,
	 * so CreateFlow passes `phase.name === "create"` — no overlay and no drop handling during
	 * progress/success (see {@link useWindowFileDrag}).
	 */
	enabled: boolean;
	/**
	 * Everything the capture could NOT carry (ui#1308) — always called once per capture, including
	 * with an empty report, because deciding whether a report is worth showing is a rule and rules do
	 * not live in this component (`core/transfer/selectionLosses#selectionLossMessage` decides).
	 */
	onLosses?: (losses: SelectionLosses) => void;
	children: React.ReactNode;
}

/**
 * Whole-page drag-and-drop. The design binds its drag handlers on `window` and the empty card
 * invites "or drop them anywhere" wherever a pointer can drag at all (ui#1306 hides that line on
 * coarse pointers), so drops are accepted across the entire viewport rather than over a card-sized
 * zone. {@link useWindowFileDrag} owns the window-level listeners
 * (SSG-safe: bound inside an effect, gated by `enabled`) and the {@link DropOverlay} is portaled
 * onto `document.body` while a file drag hovers so it covers the viewport regardless of where this
 * layer sits in the layout. Click-to-browse is exposed via `useBrowse()` (Browse files / add-more
 * buttons).
 *
 * Folder structure (ui#94): both pickers wrap their `File`s through {@link fromPickedFiles} (which
 * reads `webkitRelativePath`), and dropped folders arrive from the drag hook already traversed with
 * paths; everything merges path-aware.
 *
 * TWO hidden inputs, because one input cannot offer both choosers: the second carries
 * `webkitdirectory` and is opened by `browseFolder()` from the "Add a folder" buttons (owner decision
 * 2026-09-15, ui#1314). The first must stay FIRST in the DOM — several specs reach the file picker as
 * `input[type="file"]` `.first()`. An engine without directory picking needs no fallback branch here:
 * it ignores the unknown attribute and the same input opens an ordinary multi-file picker, and if it
 * instead accepts the attribute and returns nothing (iOS Safari before 18.4), the empty result is
 * reported as a loss rather than looking like a dead button — see `selectionLosses#CaptureSource`.
 */
export function PageDropLayer({selection, onSelectionChange, enabled, onLosses, children}: Props) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const folderInputRef = useRef<HTMLInputElement>(null);

	const addCapture = (
		incoming: SelectedFile[],
		foldersWithoutFiles: readonly string[],
		source: CaptureSource,
	) => {
		onLosses?.(selectionLossesOf(incoming, foldersWithoutFiles, source));
		if (incoming.length) onSelectionChange(mergeSelectedFiles(selection, incoming));
	};

	const isDragActive = useWindowFileDrag({
		enabled,
		onDrop: ({files, foldersWithoutFiles}: DroppedCapture) =>
			addCapture(files, foldersWithoutFiles, "drop"),
	});

	/**
	 * Both pickers, distinguished only by the `source` they report: what a capture could not carry
	 * differs per surface and deciding that is the domain's job, not this component's.
	 */
	const onPicked = (source: CaptureSource) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = fromPickedFiles(e.target.files);
		// Cleared so re-picking the SAME folder fires `change` again; read the files first.
		e.target.value = "";
		// No folder NAMES either way: a picker reports none, unlike the drop traversal. The folder
		// input's own empty result is still a loss, which `selectionLossesOf` derives from the source.
		addCapture(picked, [], source);
	};

	return (
		<BrowseContext.Provider value={{
			browseFiles: () => fileInputRef.current?.click(),
			browseFolder: () => folderInputRef.current?.click(),
		}}>
			<div style={{width: "100%", height: "100%"}}>
				<input ref={fileInputRef} type="file" hidden multiple onChange={onPicked("filePick")} />
				<input ref={folderInputRef} type="file" hidden multiple webkitdirectory=""
					   onChange={onPicked("folderPick")} />
				{children}
				{isDragActive && createPortal(<DropOverlay/>, document.body)}
			</div>
		</BrowseContext.Provider>
	);
}
